import React, { useState, useEffect } from 'react';
import { ArrowRight, Eye, Zap, Heart, Quote, Flower, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const QUOTES = [
  { text: "The moon does not fight. It attacks no one. It does not worry. It does not try to crush others. It keeps to its course, but by its very nature, it gently influences.", author: "Ming-Dao Deng" },
  { text: "Only staying active will make you want to live a hundred years.", author: "Japanese Proverb" },
  { text: "If you try to catch two rabbits, you will not catch either.", author: "Confucius" },
  { text: "Nunchi is the ability to gauge the mood of others and read the atmosphere.", author: "Euny Hong" },
  { text: "He who knows others is wise; he who knows himself is enlightened.", author: "Lao Tzu" },
  { text: "Your ikigai is at the intersection of what you are good at and what you love doing.", author: "Hector Garcia" },
  { text: "Walk slowly and you'll go far.", author: "Ikigai Rule" }
];

const Home: React.FC = () => {
  const [quote, setQuote] = useState(QUOTES[0]);

  useEffect(() => {
    // Random quote on mount
    setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center space-y-12 animate-fadeIn pb-20">
      
      {/* Hero Section */}
      <section className="text-center max-w-2xl px-6 pt-10">
        <div className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-wider text-nunchi-600 uppercase bg-nunchi-100 rounded-full">
          The Art of Situational Awareness
        </div>
        <h1 className="text-5xl md:text-7xl font-serif font-bold text-nunchi-900 mb-6 tracking-tight">
          Master Your <br/> <span className="text-nunchi-500 italic">Nunchi</span>
        </h1>
        <p className="text-lg md:text-xl text-nunchi-600 leading-relaxed mb-8">
          Nunchi (눈치) is the Korean superpower of reading the room. 
          It's the ability to gauge the moods of others and react appropriately. 
          Sharpen your senses and build deeper connections.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/scenario" 
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-base font-semibold text-white bg-nunchi-700 rounded-xl shadow-lg hover:bg-nunchi-800 transition-all hover:scale-105"
          >
            Start Training <ArrowRight size={20} />
          </Link>
          <Link 
            to="/learn" 
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-base font-semibold text-nunchi-700 bg-white border border-nunchi-200 rounded-xl hover:bg-nunchi-50 transition-all"
          >
            Read Principles
          </Link>
        </div>
      </section>

      {/* Daily Inspiration */}
      <section className="w-full max-w-3xl px-6">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-nunchi-200 relative overflow-hidden text-center">
           <Quote className="absolute top-4 left-4 text-nunchi-100" size={48} />
           <p className="relative text-lg md:text-xl font-serif text-nunchi-800 italic mb-4 z-10">
             "{quote.text}"
           </p>
           <p className="text-sm font-bold text-nunchi-500 uppercase tracking-widest">
             — {quote.author}
           </p>
        </div>
      </section>

      {/* Nunchi vs Ikigai Connection */}
      <section className="w-full max-w-4xl px-6">
        <div className="bg-gradient-to-br from-nunchi-800 to-nunchi-900 rounded-3xl p-8 md:p-12 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <h2 className="text-3xl font-serif font-bold mb-4">Harmonizing Inner & Outer Worlds</h2>
              <p className="text-nunchi-200 leading-relaxed mb-6">
                Complete social mastery requires balance. While <strong>Nunchi</strong> helps you navigate the external world of relationships and atmosphere, <strong>Ikigai</strong> anchors you in your internal world of purpose and passion.
              </p>
              <Link to="/ikigai" className="inline-flex items-center gap-2 text-white font-bold border-b border-white/30 pb-1 hover:border-white transition-colors">
                Discover your Ikigai <ArrowUpRight size={18} />
              </Link>
            </div>
            
            <div className="flex-shrink-0 grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl text-center w-32">
                <Eye className="mx-auto mb-2 text-blue-300" size={24} />
                <div className="text-sm font-bold">Nunchi</div>
                <div className="text-[10px] text-nunchi-300">Outer Awareness</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl text-center w-32">
                <Flower className="mx-auto mb-2 text-pink-300" size={24} />
                <div className="text-sm font-bold">Ikigai</div>
                <div className="text-[10px] text-nunchi-300">Inner Purpose</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl px-6 w-full">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-nunchi-100 hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 text-blue-600">
            <Eye size={24} />
          </div>
          <h3 className="text-xl font-bold text-nunchi-900 mb-2">Eye Measure</h3>
          <p className="text-nunchi-600">
            Learn to treat every social setting like a living organism that changes constantly.
          </p>
        </div>
        
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-nunchi-100 hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4 text-amber-600">
            <Zap size={24} />
          </div>
          <h3 className="text-xl font-bold text-nunchi-900 mb-2">Speed is Key</h3>
          <p className="text-nunchi-600">
            A quick Nunchi is a good Nunchi. Practice rapid assessment of new environments.
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-nunchi-100 hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center mb-4 text-rose-600">
            <Heart size={24} />
          </div>
          <h3 className="text-xl font-bold text-nunchi-900 mb-2">Build Harmony</h3>
          <p className="text-nunchi-600">
            The goal isn't manipulation, but creating comfort and trust for everyone involved.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;